<template>
  <div class="page insights">
    <section class="ins-hero">
      <nav class="crumb">
        <router-link to="/home">首页</router-link>
        <span>/</span>
        <em>行业洞察</em>
      </nav>
      <span class="eyebrow reveal" v-reveal>Insights</span>
      <h1 class="h2 ins-title reveal" v-reveal="{ delay: 60 }">行业洞察</h1>
      <p class="ins-lead reveal" v-reveal="{ delay: 120 }">
        把一线实施里踩过的坑、算过的账、验收过的标准写下来。不做概念科普，
        每篇都按「问题—判断依据—怎么落地」写，方便你拿去和内部讨论。
      </p>
    </section>

    <section class="ins-filter reveal" v-reveal>
      <button
        v-for="c in categories"
        :key="c.key"
        class="ins-tab"
        :class="{ active: active === c.key }"
        @click="active = c.key"
      >
        {{ c.name }}
        <span class="ins-tab-num">{{ countOf(c.key) }}</span>
      </button>
    </section>

    <section class="ins-grid">
      <router-link
        v-for="(p, i) in filtered"
        :key="p.slug"
        :to="`/insights/${p.slug}`"
        class="ins-card reveal"
        v-reveal="{ delay: (i % 3) * 70 }"
      >
        <div class="ins-card-top">
          <span class="ins-chip">{{ p.category }}</span>
          <span class="ins-mins">{{ p.minutes }} 分钟</span>
        </div>
        <h2 class="ins-card-title">{{ p.title }}</h2>
        <p class="ins-card-desc">{{ p.description }}</p>
        <div class="ins-card-foot">
          <span class="ins-date">{{ p.date }}</span>
          <span class="ins-more">阅读全文 <IconSvg name="arrow-right"/></span>
        </div>
      </router-link>
    </section>

    <CtaBand
      eyebrow="Get Started"
      title="这些判断放到你的现场是否成立？"
      desc="把地块、车间或产线现状讲给我们听，我们先判断可行性，再谈要不要做、怎么做。"
    />
  </div>
</template>

<script>
import IconSvg from '@/components/common/IconSvg'
import CtaBand from '@/components/common/CtaBand'
import { posts } from '@/data/posts'

/**
 * 行业洞察列表页：SEO 长文的站内入口。
 * 内容来自 src/data/posts.js（由 build_posts.py 从 SEO 长文生成）。
 */
export default {
  name: 'InsightsPage',
  components: { IconSvg, CtaBand },
  data() {
    return {
      posts,
      active: 'all',
      categories: [
        { key: 'all', name: '全部' },
        { key: 'mes', name: 'MES 生产执行系统' },
        { key: 'agriculture', name: '智慧农业' }
      ]
    }
  },
  computed: {
    filtered() {
      return this.active === 'all'
        ? this.posts
        : this.posts.filter((p) => p.categoryKey === this.active)
    }
  },
  methods: {
    countOf(key) {
      return key === 'all'
        ? this.posts.length
        : this.posts.filter((p) => p.categoryKey === key).length
    }
  }
}
</script>

<style lang="scss" scoped>
.insights { padding-top: 132px; padding-bottom: 40px; }

.ins-hero {
  max-width: 860px;
  margin-bottom: 34px;

  .crumb {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 18px;
    font-size: 13px;
    color: var(--txt-3);
    a { color: var(--txt-2); }
    a:hover { color: var(--cyan); }
    em { font-style: normal; color: var(--txt-2); }
  }
}

.ins-title { margin: 10px 0 14px; }

.ins-lead {
  margin: 0;
  max-width: 760px;
  font-size: 15.5px;
  line-height: 1.85;
  color: var(--txt-2);
}

.ins-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 30px;
}

.ins-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.03);
  color: var(--txt-2);
  font-size: 14px;
  cursor: pointer;
  transition: color 0.3s, border-color 0.3s, background 0.3s;

  &:hover { color: #fff; border-color: var(--line-strong); }
  &.active {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(120deg, var(--cyan), var(--blue));
  }

  .ins-tab-num {
    font-size: 12px;
    opacity: 0.72;
  }
}

.ins-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
  margin-bottom: 70px;
}

.ins-card {
  display: flex;
  flex-direction: column;
  padding: 26px 26px 24px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: linear-gradient(160deg, rgba(13, 27, 54, 0.86), rgba(6, 13, 29, 0.86));
  transition: transform 0.45s cubic-bezier(0.2, 0.7, 0.2, 1), border-color 0.45s, box-shadow 0.45s;

  &:hover {
    transform: translateY(-8px);
    border-color: var(--line-strong);
    box-shadow: 0 30px 60px -34px rgba(34, 224, 255, 0.6);
    .ins-more svg { transform: translateX(4px); }
  }
}

.ins-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}

.ins-chip {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  letter-spacing: 0.02em;
  color: var(--cyan);
  background: rgba(34, 224, 255, 0.1);
  border: 1px solid rgba(34, 224, 255, 0.22);
}

.ins-mins { font-size: 12px; color: var(--txt-3); }

.ins-card-title {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.55;
  color: #fff;
}

.ins-card-desc {
  margin: 0 0 20px;
  flex: 1;
  font-size: 14px;
  line-height: 1.85;
  color: var(--txt-2);
}

.ins-card-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid rgba(120, 176, 255, 0.1);
  font-size: 12.5px;
  color: var(--txt-3);
}

.ins-more {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--accent-txt);
  svg { width: 15px; height: 15px; transition: transform 0.35s; }
}

@media (max-width: 1180px) {
  .ins-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 760px) {
  .insights { padding-top: 108px; }
  .ins-grid { grid-template-columns: minmax(0, 1fr); gap: 16px; }
  .ins-card { padding: 22px; }
}
</style>
